/**
 * useNewTaskDots — compute "has new tasks" indicators for sidebar items.
 *
 * Returns a set of view keys that have tasks created after the user's
 * last visit. Used for red-dot indicators in the sidebar.
 *
 * Performance: single IDB query for all unfinished tasks created after
 * the oldest lastSeen timestamp, then bucketed in JS. Typically <5ms.
 *
 * Only tracks:
 *   - Inbox: new tasks in inbox since last visit
 *   - Projects/Lists: new tasks added since last visit
 *
 * Date-based views (Today, Upcoming, etc.) are NOT tracked — they change
 * daily by design and dots would be noisy.
 */

import { useLiveQueryImmediate } from "@/db/hooks"
import { db, ObjectType, ObjectStatus } from "@/db/schema"
import {
  useViewSeenStore,
  viewSeenKey,
} from "@/features/views/stores/viewSeenStore"
import type { MenuItem } from "@/features/static-data/useStaticData"

/**
 * Compute which sidebar items have new tasks since last visit.
 *
 * @param menuItems - Dynamic sidebar items (projects, lists, areas)
 * @returns Set of view keys that have unseen new tasks
 */
export function useNewTaskDots(menuItems: MenuItem[]): Set<string> {
  const timestamps = useViewSeenStore((s) => s.timestamps)
  const hydrated = useViewSeenStore((s) => s.hydrated)

  const [dots] = useLiveQueryImmediate(
    async () => {
      if (!hydrated) return new Set<string>()

      const result = new Set<string>()

      // ── Inbox dot ──
      const inboxLastSeen = timestamps[viewSeenKey("inbox")]
      if (inboxLastSeen) {
        const currentUserId = db.cloud?.currentUserId
        // getAll() + JS filter instead of .filter().count(), which walks a
        // cursor with a main-thread round trip per inbox task.
        const newInboxCount = (
          await db.totodo
            .where("[parentPath+status+isDeleted]")
            .equals(["root/#inbox", ObjectStatus.Unfinished, 0])
            .toArray()
        ).filter(
          (t) =>
            t.type === ObjectType.Task &&
            !!t.createdAt &&
            t.createdAt > inboxLastSeen &&
            // Don't dot for tasks the current user created themselves
            (!currentUserId || t.owner !== currentUserId),
        ).length

        if (newInboxCount > 0) {
          result.add("inbox")
        }
      }

      // ── Container dots (projects / lists) ──
      // Only check containers that have a lastSeen timestamp
      const containerChecks: Array<{
        key: string
        parentPath: string
        lastSeen: string
      }> = []

      for (const item of menuItems) {
        if (item.type !== ObjectType.Project && item.type !== ObjectType.List) {
          continue
        }

        const key = viewSeenKey(
          item.type === ObjectType.Project ? "project" : "list",
          item.id,
        )
        const lastSeen = timestamps[key]
        if (lastSeen) {
          containerChecks.push({
            key,
            parentPath: `root/${item.id}`,
            lastSeen,
          })
        }
      }

      // Batch: query all child tasks for containers with timestamps
      // Use a single broad query and filter in JS for efficiency
      if (containerChecks.length > 0) {
        // Find the oldest lastSeen to use as lower bound
        const oldestLastSeen = containerChecks.reduce(
          (min, c) => (c.lastSeen < min ? c.lastSeen : min),
          containerChecks[0]!.lastSeen,
        )

        // Single query: all unfinished tasks created after oldest lastSeen
        const currentUserId = db.cloud?.currentUserId
        const recentTasks = await db.totodo
          .where("createdAt")
          .above(oldestLastSeen)
          .filter(
            (t) =>
              t.type === ObjectType.Task &&
              t.status === ObjectStatus.Unfinished &&
              t.isDeleted === 0 &&
              // Don't dot for tasks the current user created themselves
              (!currentUserId || t.owner !== currentUserId),
          )
          .toArray()

        // Bucket by parentPath
        const byParent = new Map<string, boolean>()
        for (const task of recentTasks) {
          const pp = task.parentPath
          for (const check of containerChecks) {
            if (
              pp.startsWith(check.parentPath) &&
              task.createdAt! > check.lastSeen
            ) {
              byParent.set(check.key, true)
            }
          }
        }

        for (const [key, hasNew] of byParent) {
          if (hasNew) result.add(key)
        }
      }

      return result
    },
    ["new-task-dots", hydrated, JSON.stringify(timestamps), menuItems.length],
    new Set<string>(),
  )

  return dots ?? new Set<string>()
}
